import { useCallback, useState, useEffect } from "react";
import DarkFileUploader from "./components/DarkFileUploader";
import ConversionResults from "./components/ConversionResults";
import APISettingsPanel from "./components/APISettingsPanel";
import MediaLibrary from "./components/MediaLibrary";
import { Tabs } from "@mui/base/Tabs";
import { TabsList } from "@mui/base/TabsList";
import { Tab } from "@mui/base/Tab";
import { Sparkles } from "lucide-react";
import { FalAPIService } from "./services/falAPI";
import { compressImage } from "./lib/imageUtils";
import {
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
} from "@mui/material";
import { FAL_KEY, URLS } from "../../constants";
import { uploader } from "../../utils/uploader";
import "./aiStudio.css";
import RenderSnackBar from "../../components/UI/SnackBar/RenderSnackBar";
import SavedPrompts from "../Social/SocialOperator/SavedPrompts";

const apis = [
  {
    id: "pixverse-image-to-video",
    name: "Pixverse Image to Video",
    description: "Convert images to animated videos",
    type: "image",
    provider: "Pixverse",
  },
  {
    id: "pixverse-transition",
    name: "Pixverse Transition",
    description: "Smooth transitions between images",
    type: "image",
    provider: "Pixverse",
  },
  {
    id: "flux-pro-kontext",
    name: "Flux Pro Kontext",
    description: "High-quality image generation with context",
    type: "image",
    provider: "Flux",
  },
  {
    id: "nano-banana",
    name: "Nano Banana",
    description: "Google's state-of-the-art image generation and editing model",
    type: "image",
    provider: "Fal",
  },
  {
    id: "fal-ai-vidu",
    name: "Reference to Video",
    description: "Reference-to-video generation",
    type: "video",
    provider: "Fal AI",
  },
];
const autoSaveApiIds = [
  "pixverse-image-to-video",
  "pixverse-transition",
  "fal-ai-vidu",
];
const AiStudio = ({ eventDetails, loggedInUser }) => {
  const eventId = eventDetails.event?._id || "";
  const _eventDetails = {
    ...eventDetails,
    loggedInUserObj: loggedInUser,
  };
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedAPI, setSelectedAPI] = useState(apis[0] || null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState([]);
  const [apiKey, setAPIKey] = useState(FAL_KEY);
  const [apiSettings, setApiSettings] = useState({});
  const [isSaving, setIsSavinng] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);

  const [selectedPrompt, setSelectedPrompt] = useState(null);

  // Add base64 images from selectedPrompt.sourceImageUrls to uploadedFiles
  useEffect(() => {
    if (
      selectedPrompt &&
      Array.isArray(selectedPrompt.sourceImageUrls) &&
      selectedPrompt.sourceImageUrls.length > 0
    ) {
      // Only add images that are not already in uploadedFiles (by base64 string)
      const existingBase64s = uploadedFiles
        .filter((f) => f.preview && f.preview.startsWith("data:image"))
        .map((f) => f.preview);

      const newFiles = selectedPrompt.sourceImageUrls
        .filter((src) => src && !existingBase64s.includes(src))
        .map((src) => {
          if (!src) return src; // undefined, null, or empty string
          // Check if src is a valid data URL (base64)
          if (typeof src === "string" && src.startsWith("data:image")) {
            const base64Part = src.split(",")[1];
            if (!base64Part) return src;
            let file = null;
            if (base64Part) {
              try {
                const mimeType = src.substring(
                  src.indexOf(":") + 1,
                  src.indexOf(";")
                );
                const byteString = atob(base64Part);
                const ab = new ArrayBuffer(byteString.length);
                const ia = new Uint8Array(ab);
                for (let i = 0; i < byteString.length; i++) {
                  ia[i] = byteString.charCodeAt(i);
                }
                file = new File(
                  [ab],
                  `source-image-${Math.random().toString(36).substr(2, 9)}.png`,
                  { type: mimeType }
                );
              } catch (e) {
                return src;
              }
            } else {
              file = src;
            }

            const id = Math.random().toString(36).substr(2, 9);
            const preview = src;
            return { id, file, preview, type: "image", isFromPrompt: true };
          }
          // Check if src is a valid image URL
          if (
            typeof src === "string" &&
            /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp)$/i.test(src)
          ) {
            // Fetch the image and create a File object
            // Note: This is async, but useEffect expects sync. So, just return src for now or handle async elsewhere.
            // If you want to handle async, you need to refactor useEffect to async and use Promise.all
            const id = Math.random().toString(36).substr(2, 9);
            const preview = src;
            return {
              id,
              file: src,
              preview,
              type: "image",
              isFromPrompt: true,
            };
          }
          // Otherwise, return src as is
          return src;
        });
      if (newFiles.length > 0) {
        setUploadedFiles((prev) => {
          // Merge and ensure uniqueness by file property if file is a valid URL
          const allFiles = [...prev, ...newFiles];
          const seenUrls = new Set();
          return allFiles.filter((f) => {
            if (
              f.file &&
              typeof f.file === "string" &&
              /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp)$/i.test(f.file)
            ) {
              if (seenUrls.has(f.file)) return false;
              seenUrls.add(f.file);
              return true;
            }
            return true;
          });
        });
      }
    } else {
      // If no new files were added, we can reset the uploadedFiles
      setUploadedFiles((pre) => {
        return pre.filter((f) => !f.isFromPrompt);
      });
    }
  }, [selectedPrompt]);
  const [generateExact, setGenerateExact] = useState(false);

  const handleSelectPrompt = useCallback((newPrompt) => {
    setSelectedPrompt(newPrompt);
    setShowPrompts(false);
  }, []);

  const handleFilesUpload = (files) => {
    setUploadedFiles(files);
    setResults([]);
  };

  const handleMediaSelect = (files) => {
    setUploadedFiles((prev) => [...prev, ...files]);
    setResults([]);
  };

  const handleAPISelect = (api) => {
    setSelectedAPI(api);
    setApiSettings({});
    setUploadedFiles([]);
    setResults([]);
  };

  const handleSettingsChange = useCallback((settings) => {
    setApiSettings((prev) => ({
      ...prev,
      ...settings,
    }));
  }, []);

  const saveConvertedMedia = async (saveDestination, convertedUrl, apiName) => {
    try {
      setIsSavinng(true);
      const filename = `${apiName.replace(/\s+/g, "-")}-${Date.now()}.${
        convertedUrl.includes(".mp4") ? "mp4" : "png"
      }`;
      const mimeType = convertedUrl.includes(".mp4")
        ? "video/mp4"
        : "image/png";

      // Fetch the converted media as a Blob
      const response = await fetch(convertedUrl);
      const blob = await response.blob();

      // Create a File object from the Blob
      const fileBlob = new File([blob], filename, { type: mimeType });

      const payload = {
        comment: `AI Generated: ${apiName}`,
        eventId: eventId,
        userId: loggedInUser?.email || "",
        mimeType: mimeType,
      };
      if (saveDestination === 1) {
        payload["isReviewed"] = false;
      }
      if (saveDestination === 5) {
        payload["isProductionUploads"] = true; // Save to Production Media
      }
      if (saveDestination === 6) {
        payload["isMediaLibrary"] = true; // Save to Media Library
      }
      if (saveDestination === 3) {
        payload["isAiGenerated"] = true; // Save to AI Approved Submissions
      }
      if (saveDestination === 2 || saveDestination === 3) {
        payload["isReviewed"] = true; // Save to Approved Submissions
      }

      payload["mediaFile"] = fileBlob;

      const uploadResponse = await uploader({
        url: URLS.UPLOAD_MEDIA(),
        body: payload,
      });

      if (uploadResponse.status === 201) {
        console.log(
          "Converted media saved to production:",
          uploadResponse.sharedMedia
        );
      } else {
        console.error(
          "Failed to save converted media to production:",
          uploadResponse
        );
      }
      setIsSavinng(false);
    } catch (error) {
      setIsSavinng(false);
      console.error("Error saving converted media to production:", error);
    }
  };

  const handleConvert = async () => {
    if (!selectedAPI || uploadedFiles.length === 0 || !apiKey) {
      console.log("Missing requirements:", {
        selectedAPI: !!selectedAPI,
        uploadedFiles: uploadedFiles.length,
        apiKey: !!apiKey,
      });
      return;
    }

    // Special validation for Pixverse Transition
    if (selectedAPI.id === "pixverse-transition" && uploadedFiles.length < 2) {
      console.error("Pixverse Transition requires at least 2 images");
      alert(
        "Pixverse Transition requires at least 2 images to create a transition between them."
      );
      return;
    }

    console.log("Starting conversion with:", {
      api: selectedAPI.id,
      fileCount: uploadedFiles.length,
      settings: apiSettings,
    });
    if (autoSaveApiIds.includes(selectedAPI.id)) {
      setShowToast(true);
    } else {
      setShowToast(false);
    }
    setIsProcessing(true);

    try {
      const falService = new FalAPIService(apiKey);
      const mockResults = [];

      if (selectedAPI.id === "pixverse-transition") {
        console.log(
          "Processing Pixverse Transition with",
          uploadedFiles.length,
          "images"
        );

        // Handle Pixverse Transition specially - it needs two images
        const firstImageDataUrl = await compressImage(
          uploadedFiles[0].file,
          1024,
          0.95
        );
        const lastImageDataUrl = await compressImage(
          uploadedFiles[1].file,
          1024,
          0.95
        );

        console.log("Compressed images for Pixverse Transition");

        // Build params according to API documentation
        const params = {
          prompt:
            apiSettings.prompt ||
            `Create a smooth transition between these images`,
          first_image_url: firstImageDataUrl,
          end_image_url: lastImageDataUrl,
          aspect_ratio: apiSettings.aspect_ratio || "16:9",
        };

        // Only add optional parameters if they have valid values
        if (
          apiSettings.style &&
          apiSettings.style.trim() &&
          apiSettings.style !== ""
        ) {
          params.style = apiSettings.style;
        }

        if (
          apiSettings.negative_prompt &&
          apiSettings.negative_prompt.trim() &&
          apiSettings.negative_prompt !== ""
        ) {
          params.negative_prompt = apiSettings.negative_prompt;
        }

        if (
          apiSettings.seed &&
          !isNaN(parseInt(apiSettings.seed)) &&
          apiSettings.seed !== ""
        ) {
          params.seed = parseInt(apiSettings.seed);
        }

        console.log("Sending Pixverse Transition params:", params);

        try {
          const { result, inputs } = await falService.generateImage(
            selectedAPI.id,
            params
          );
          console.log("Pixverse Transition result:", result);

          mockResults.push({
            id: "pixverse-transition-result",
            originalFile: uploadedFiles[0],
            convertedUrl:
              result.video?.url ||
              result.images?.[0]?.url ||
              uploadedFiles[0].preview,
            status: "completed",
            api: selectedAPI,
            inputs,
            seed: result?.seed || null,
          });
        } catch (error) {
          console.error("API error for Pixverse Transition:", error);
          mockResults.push({
            id: "pixverse-transition-result",
            originalFile: uploadedFiles[0],
            convertedUrl: uploadedFiles[0].preview,
            status: "error",
            api: selectedAPI,
            error: error instanceof Error ? error.message : "Unknown error",
            inputs: {},
            seed: null,
          });
        }
      } else if (selectedAPI.id === "flux-pro-kontext") {
        console.log(
          "Processing Flux Pro Kontext with",
          uploadedFiles.length,
          "images"
        );

        // Handle Flux Pro Kontext specially - it should combine multiple images into one result
        const imageDataUrls = await Promise.all(
          uploadedFiles.map(async (file, index) => {
            if (
              typeof file.file === "string" &&
              /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp)$/i.test(file.file)
            ) {
              return file.file;
            }
            console.log(
              `Compressing image ${index + 1}/${uploadedFiles.length}: ${
                file.file.name
              }`
            );
            // Use higher quality for Kontext to avoid splitting issues
            const compressed = await compressImage(file.file, 1024, 0.95);
            console.log(`Successfully compressed ${file.file.name}`);
            return compressed;
          })
        );

        console.log(
          "Successfully compressed",
          imageDataUrls.length,
          "images for Flux Pro Kontext"
        );

        const params = {
          prompt:
            apiSettings.prompt ||
            `Transform and combine these ${uploadedFiles.length} images into a cohesive result`,
          image_urls: imageDataUrls, // Send all images as an array
          strength: apiSettings.strength || 0.8,
          num_inference_steps: apiSettings.numSteps || 20,
          guidance_scale: apiSettings.guidanceScale || 7.5,
          aspect_ratio: apiSettings.aspectRatio || "1:1",
          num_images: 1, // Explicitly set to return only 1 image
          safety_tolerance: apiSettings.safetyTolerance || 5,
          enable_safety_checker: false,
        };

        // Only add optional parameters if they have valid values
        if (
          apiSettings.negativePrompt &&
          apiSettings.negativePrompt.trim() &&
          apiSettings.negativePrompt !== ""
        ) {
          params.negativePrompt = apiSettings.negativePrompt;
        }

        if (
          apiSettings.seed &&
          !isNaN(parseInt(apiSettings.seed)) &&
          apiSettings.seed !== ""
        ) {
          params.seed = parseInt(apiSettings.seed);
        }

        console.log("Sending Flux Pro Kontext params:", params);

        try {
          const { result, inputs } = await falService.generateImage(
            selectedAPI.id,
            params
          );
          console.log("Flux Pro Kontext result:", result);

          // Determine the correct URL based on response type
          let convertedUrl = uploadedFiles[0].preview; // fallback
          if (result.video?.url) {
            convertedUrl = result.video.url;
          } else if (result.images && result.images.length > 0) {
            convertedUrl = result.images[0].url;
            console.log("Using image URL:", convertedUrl);
          }

          mockResults.push({
            id: "flux-pro-kontext-result",
            originalFile: uploadedFiles[0], // Use first file as reference
            convertedUrl: convertedUrl,
            status: "completed",
            api: selectedAPI,
            inputs,
            seed: result?.seed || null,
          });
        } catch (error) {
          console.error("API error for Flux Pro Kontext:", error);
          mockResults.push({
            id: "flux-pro-kontext-result",
            originalFile: uploadedFiles[0],
            convertedUrl: uploadedFiles[0].preview,
            status: "error",
            api: selectedAPI,
            error: error instanceof Error ? error.message : "Unknown error",
            inputs: {},
            seed: null,
          });
        }
      } else if (selectedAPI.id === "nano-banana") {
        console.log(
          "Processing Nano banana image editing with",
          uploadedFiles.length,
          "images"
        );

        // Handle Nano banana image editing specially - it should combine multiple images into one result
        const imageDataUrls = await Promise.all(
          uploadedFiles.map(async (file, index) => {
            if (
              typeof file.file === "string" &&
              /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp)$/i.test(file.file)
            ) {
              return file.file;
            }
            console.log(
              `Compressing image ${index + 1}/${uploadedFiles.length}: ${
                file.file.name
              }`
            );
            // Use higher quality for Kontext to avoid splitting issues
            const compressed = await compressImage(file.file, 1024, 0.95);
            console.log(`Successfully compressed ${file.file.name}`);
            return compressed;
          })
        );

        console.log(
          "Successfully compressed",
          imageDataUrls.length,
          "images for Flux Pro Kontext"
        );

        const params = {
          prompt:
            apiSettings.prompt ||
            `Transform and combine these ${uploadedFiles.length} images into a cohesive result`,
          image_urls: imageDataUrls, // Send all images as an array
          num_images: 1, // Explicitly set to return only 1 image
        };

        // Only add optional parameters if they have valid values
        if (
          apiSettings.negativePrompt &&
          apiSettings.negativePrompt.trim() &&
          apiSettings.negativePrompt !== ""
        ) {
          params.negativePrompt = apiSettings.negativePrompt;
        }

        if (
          apiSettings.seed &&
          !isNaN(parseInt(apiSettings.seed)) &&
          apiSettings.seed !== ""
        ) {
          params.seed = parseInt(apiSettings.seed);
        }

        // Add aspect_ratio parameter
        if (apiSettings.aspect_ratio) {
          params.aspect_ratio = apiSettings.aspect_ratio;
        }

        console.log("Sending Nano banana image editing params:", params);

        try {
          const { result, inputs } = await falService.generateImage(
            selectedAPI.id,
            params
          );
          console.log("Nano banana image editing result:", result);

          // Determine the correct URL based on response type
          let convertedUrl = uploadedFiles[0].preview; // fallback
          if (result.video?.url) {
            convertedUrl = result.video.url;
          } else if (result.images && result.images.length > 0) {
            convertedUrl = result.images[0].url;
            console.log("Using image URL:", convertedUrl);
          }

          mockResults.push({
            id: "nano-banana-result",
            originalFile: uploadedFiles[0], // Use first file as reference
            convertedUrl: convertedUrl,
            status: "completed",
            api: selectedAPI,
            inputs,
            seed: result?.seed || null,
          });
        } catch (error) {
          console.error("API error for Nano banana image editing:", error);
          mockResults.push({
            id: "nano-banana-result",
            originalFile: uploadedFiles[0],
            convertedUrl: uploadedFiles[0].preview,
            status: "error",
            api: selectedAPI,
            error: error instanceof Error ? error.message : "Unknown error",
            inputs: {},
            seed: null,
          });
        }
      } else if (selectedAPI.id === "fal-ai-vidu") {
        console.log(
          "Processing Reference to Video with",
          uploadedFiles.length,
          "images"
        );

        const imageDataUrls = await Promise.all(
          uploadedFiles.map((file) => {
            if (
              typeof file.file === "string" &&
              /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp)$/i.test(file.file)
            ) {
              return file.file;
            }
            return compressImage(file.file, 1024, 0.9);
          })
        );

        const params = {
          prompt: apiSettings.prompt || `Animate this image`,
          image_url: imageDataUrls,
          aspect_ratio: apiSettings.aspect_ratio || "16:9",
          movement_amplitude: apiSettings.movement_amplitude || "auto",
        };

        if (
          apiSettings.negativePrompt &&
          apiSettings.negativePrompt.trim() &&
          apiSettings.negativePrompt !== ""
        ) {
          params.negative_prompt = apiSettings.negativePrompt;
        }

        if (
          apiSettings.seed &&
          !isNaN(parseInt(apiSettings.seed)) &&
          apiSettings.seed !== ""
        ) {
          params.seed = parseInt(apiSettings.seed);
        }

        console.log("Sending Reference to Video params:", params);

        try {
          const { result, inputs } = await falService.generateImage(
            selectedAPI.id,
            params
          );
          console.log("Reference to Video result:", result);

          mockResults.push({
            id: "fal-ai-vidu-result",
            originalFile: uploadedFiles[0],
            convertedUrl:
              result.video?.url ||
              result.images?.[0]?.url ||
              uploadedFiles[0].preview,
            status: "completed",
            api: selectedAPI,
            inputs,
            seed: result?.seed || null,
          });
        } catch (error) {
          console.error("API error for Reference to Video:", error);
          mockResults.push({
            id: "fal-ai-vidu-result",
            originalFile: uploadedFiles[0],
            convertedUrl: uploadedFiles[0].preview,
            status: "error",
            api: selectedAPI,
            error: error instanceof Error ? error.message : "Unknown error",
            inputs: {},
            seed: null,
          });
        }
      } else {
        // Handle other APIs normally - process each file separately with compression
        for (let i = 0; i < uploadedFiles.length; i++) {
          const file = uploadedFiles[i];
          console.log(
            `Processing file ${i + 1}/${uploadedFiles.length}:`,
            file.file.name
          );

          const imageDataUrl =
            typeof file.file === "string" &&
            /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp)$/i.test(file.file)
              ? file.file
              : await compressImage(file.file, 1024, 0.9);

          // Build params based on API requirements
          const params = {
            prompt:
              apiSettings.customPrompt ||
              apiSettings.prompt ||
              `Transform this ${file.type} using ${selectedAPI.name}`,
            image_url: imageDataUrl,
            strength: apiSettings.strength || 0.8,
            num_inference_steps: apiSettings.numInferenceSteps || 20,
            guidance_scale: apiSettings.guidanceScale || 7.5,
            aspect_ratio: apiSettings.aspectRatio || "1:1",
          };

          // Only add optional parameters if they have valid values
          if (
            apiSettings.negativePrompt &&
            apiSettings.negativePrompt.trim() &&
            apiSettings.negativePrompt !== ""
          ) {
            params.negative_prompt = apiSettings.negativePrompt;
          }

          if (
            apiSettings.seed &&
            !isNaN(parseInt(apiSettings.seed)) &&
            apiSettings.seed !== ""
          ) {
            params.seed = parseInt(apiSettings.seed);
          }

          // Add Image Editing specific params
          if (selectedAPI.id === "image-editing") {
            params.prompt = apiSettings.prompt || `Edit this image`;
            params.strength = apiSettings.strength || 0.8;
            params.num_inference_steps = apiSettings.numInferenceSteps || 20;
            params.guidance_scale = apiSettings.guidanceScale || 7.5;
            params.model = apiSettings.model || "baby-version";
          }

          // Add effect for Pixverse Effects
          if (selectedAPI.id === "pixverse-effects") {
            params.effect =
              apiSettings.presetEffect && apiSettings.presetEffect !== "none"
                ? apiSettings.presetEffect
                : "Hug"; // Default to valid effect

            // Add resolution parameter
            if (apiSettings.resolution) {
              params.resolution = apiSettings.resolution;
            }
          }

          // Add Pika Effects specific params
          if (selectedAPI.id === "pika-pikaffects") {
            params.pikaffect = apiSettings.pikaffect || "Melt";
            params.motion_strength = apiSettings.motionStrength || 0.5;
          }

          // Add Nano banana image editing specific params
          if (selectedAPI.id === "nano-banana") {
            params.id_weight = apiSettings.idWeight || 1.0;
            params.true_guidance_scale = apiSettings.trueGuidanceScale || 3.0;
            params.width = apiSettings.width || 1024;
            params.height = apiSettings.height || 1024;
            params.num_images = apiSettings.numImages || 1;
            params.enable_safety_checker =
              apiSettings.enableSafetyChecker !== false;
          }

          console.log("Sending params to API for file", i + 1, ":", params);

          try {
            const { result, inputs } = await falService.generateImage(
              selectedAPI.id,
              params
            );
            console.log("API result for file", i + 1, ":", result);

            // Determine the correct URL based on response type
            let convertedUrl = file.preview; // fallback
            if (result.video?.url) {
              convertedUrl = result.video.url;
            } else if (result.images && result.images.length > 0) {
              convertedUrl = result.images[0].url;
            }

            mockResults.push({
              id: file.id,
              originalFile: file,
              convertedUrl: convertedUrl,
              status: "completed",
              api: selectedAPI,
              inputs,
              seed: result?.seed || null,
            });
          } catch (error) {
            console.error("API error for file:", file.id, error);
            mockResults.push({
              id: file.id,
              originalFile: file,
              convertedUrl: file.preview,
              status: "error",
              api: selectedAPI,
              error: error instanceof Error ? error.message : "Unknown error",
              inputs: {},
              seed: null,
            });
          }
        }
      }

      console.log("All processing complete. Results:", mockResults);
      setResults(mockResults);
      setIsProcessing(false);
      if (autoSaveApiIds.includes(selectedAPI.id)) {
        // Automatically save results to production media
        for (const result of mockResults) {
          if (result.status === "completed") {
            await saveConvertedMedia(
              5, // Save to Production Media
              result.convertedUrl,
              selectedAPI.name
            );
          } else {
            console.error(
              `Skipping save for ${result.id} due to error:`,
              result.error
            );
          }
        }
      }
    } catch (error) {
      console.error("Conversion error:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const canConvert =
    uploadedFiles.length > 0 && selectedAPI && !isProcessing && apiKey;

  // Check if we have enough files for the selected API
  const hasEnoughFiles = () => {
    if (!selectedAPI) return false;
    if (
      selectedAPI.id === "pixverse-transition" ||
      selectedAPI.id === "fal-ai-vidu"
    ) {
      return uploadedFiles.length >= 2;
    }
    return uploadedFiles.length > 0;
  };

  return (
    <>
      <div className="bg-gray-900 text-gray-100 w-full aiStudioMainContainer">
        <div className="max-w-full mx-auto px-2 sm:px-4 py-2 h-full flex flex-col">
          {/* API Selection Tabs */}
          <Tabs
            value={selectedAPI?.id || ""}
            onChange={(event, value) => {
              const api = apis.find((a) => a.id === value);
              if (api) handleAPISelect(api);
            }}
            className="w-full mb-4"
          >
            <TabsList className="w-full inline-flex h-10 items-center justify-between rounded-md bg-muted p-1 text-muted-foreground bg-gray-800/50 rounded-lg">
              {apis.map((api) => (
                <Tab
                  key={api.id}
                  value={api.id}
                  className={`${
                    selectedAPI?.id === api.id
                      ? "selected aiToolTab"
                      : "aiToolTab"
                  } inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50`}
                >
                  {api.name}
                </Tab>
              ))}
            </TabsList>

            {/* API Description with integrated API Key status */}
            {selectedAPI && (
              <div className="mt-3 p-2 bg-gray-800/50 border border-gray-700 rounded-lg flex justify-between items-center">
                <div className="flex items-center justify-between gap-4">
                  <p className="text-gray-300 text-sm">
                    <span className="font-medium text-teal-400">
                      {selectedAPI.name}:
                    </span>{" "}
                    {selectedAPI.description}
                  </p>
                </div>

                <div className="flex items-center gap-6">
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      setShowPrompts(true);
                    }}
                  >
                    Select Prompt
                  </Button>
                  {selectedPrompt && (
                    <>
                      <div className="flex items-center gap-2">
                        {/* {selectedPrompt?.name ? "Using prompt" : ""} */}
                        {selectedPrompt?.exampleImageUrl ? (
                          selectedPrompt.exampleImageUrl.includes(".mp4") ? (
                            <video
                              src={selectedPrompt.exampleImageUrl}
                              className="w-auto h-[48px] object-cover rounded-md"
                            ></video>
                          ) : (
                            <img
                              src={selectedPrompt.exampleImageUrl}
                              alt="Prompt Example"
                              className="w-auto h-[48px] object-cover rounded-md"
                            />
                          )
                        ) : null}
                        <div className="text-white">
                          {selectedPrompt?.name || ""}
                        </div>
                      </div>

                      <FormControlLabel
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        label="Generate exact style?"
                        control={
                          <Checkbox
                            checked={generateExact}
                            onChange={(e) => {
                              setGenerateExact(e.target.checked);
                            }}
                            color="primary"
                            sx={{
                              color: "#24B8E7",
                            }}
                          />
                        }
                      ></FormControlLabel>
                      {selectedPrompt && (
                        <Button
                          variant="outlined"
                          color="secondary"
                          sx={{
                            color: "#f3f4f6",
                            borderColor: "#f3f4f6",
                          }}
                          onClick={() => {
                            setSelectedPrompt(null);
                            setGenerateExact(false);
                          }}
                        >
                          Clear
                        </Button>
                      )}
                    </>
                  )}
                </div>
                <Dialog
                  open={showPrompts}
                  onClose={() => setShowPrompts(false)}
                  sx={{
                    "& .MuiDialog-paper": {
                      backgroundColor: "#222934", // Dark background
                      color: "#f3f4f6", // Light text color
                      maxWidth: "800px", // Responsive max width
                    },
                  }}
                >
                  <DialogContent>
                    <SavedPrompts
                      handleSelectPrompt={handleSelectPrompt}
                      eventDetails={_eventDetails}
                      apiId={selectedAPI?.id || null}
                      detailsView={true}
                    />
                  </DialogContent>
                </Dialog>
              </div>
            )}

            {/* Show API key integration when no API is selected */}
            {!selectedAPI && (
              <div className="mt-3 p-3 bg-gray-800/50 border border-gray-700 rounded-lg">
                <div className="flex items-center justify-between gap-4">
                  <p className="text-gray-300 text-sm">
                    Select an AI service above to get started
                  </p>
                </div>
              </div>
            )}
          </Tabs>

          {/* Main Content Area - Fixed height to prevent expansion */}
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-2 flex-1 ai-studio-grid">
            {/* Left Column: Media Library - 1 column */}
            <div className="h-full overflow-y-auto">
              <MediaLibrary
                onMediaSelect={handleMediaSelect}
                eventId={eventId}
                loggedInUserObj={loggedInUser}
              />
            </div>

            {/* Middle Left Column: Settings - 2 columns for more width */}
            <div className="lg:col-span-2 h-full overflow-y-auto">
              <Card className="p-4 bg-gray-800 border-gray-700 h-full overflow-y-auto uv-card">
                <h2 className="text-xl font-semibold text-white mb-2">
                  {selectedAPI
                    ? `${selectedAPI.name} Settings`
                    : "Select an AI Service"}
                </h2>
                {selectedAPI?.id === "pixverse-transition" && (
                  <div className="mb-4 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                    <p className="text-blue-300 text-sm">
                      <strong>Note:</strong> Pixverse Transition requires at
                      least 2 images to create a transition between them. Upload
                      multiple images and the first two will be used.
                      {uploadedFiles.length > 0 && uploadedFiles.length < 2 && (
                        <span className="block mt-1 text-yellow-300">
                          Currently uploaded: {uploadedFiles.length}/2 images
                        </span>
                      )}
                    </p>
                  </div>
                )}
                {selectedAPI?.id === "fal-ai-vidu" && (
                  <div className="mb-4 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                    <p className="text-blue-300 text-sm">
                      <strong>Note:</strong> Reference to Video requires at
                      least 2 images to create a video.
                      {uploadedFiles.length > 0 && (
                        <span className="block mt-1 text-yellow-300">
                          Currently uploaded: {uploadedFiles.length}{" "}
                          {`image${uploadedFiles.length > 1 ? "s" : ""}`}
                        </span>
                      )}
                    </p>
                  </div>
                )}
                {selectedAPI?.id === "flux-pro-kontext" && (
                  <div className="mb-4 p-3 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                    <p className="text-purple-300 text-sm">
                      <strong>Note:</strong> Flux Pro Kontext combines multiple
                      uploaded images into a single result. Upload multiple
                      images to see them merged together.
                      {uploadedFiles.length > 0 && (
                        <span className="block mt-1 text-teal-300">
                          Currently uploaded: {uploadedFiles.length} images
                        </span>
                      )}
                    </p>
                  </div>
                )}
                <APISettingsPanel
                  selectedAPI={selectedAPI}
                  onSettingsChange={handleSettingsChange}
                  selectedPrompt={selectedPrompt}
                  generateExact={generateExact}
                />
              </Card>
            </div>

            {/* Right Three Columns: Upload, Generate, and Results - Fixed structure */}
            <div className="lg:col-span-3 h-full flex flex-col space-y-2 overflow-y-auto">
              {/* Upload Media Row - Reverted to original flexible height */}
              <Card className="p-4 bg-gray-800 border-gray-700 flex-shrink-0 uv-card">
                <h2 className="text-lg font-semibold text-white mb-3">
                  Upload Media
                </h2>
                <DarkFileUploader
                  onFilesUpload={handleFilesUpload}
                  uploadedFiles={uploadedFiles}
                  selectedAPI={selectedAPI}
                />
              </Card>

              {/* Generate Button Row - Fixed height */}
              <Card className="p-2 bg-gray-800 border-gray-700 flex-shrink-0 flex flex-col justify-center uv-card">
                <Button
                  onClick={handleConvert}
                  disabled={!canConvert || !hasEnoughFiles()}
                  variant="contained"
                  color="primary"
                  className={`w-full ${
                    !canConvert || !hasEnoughFiles()
                      ? "text-gray-400"
                      : "text-white"
                  }`}
                  sx={{
                    color: "white",
                  }}
                >
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate
                  </>
                </Button>

                {!canConvert && (
                  <p className="text-xs text-gray-400 text-center mt-2">
                    {!apiKey
                      ? "Set your Fal API key to continue"
                      : !selectedAPI
                      ? "Select an AI service to continue"
                      : !hasEnoughFiles()
                      ? selectedAPI.id === "pixverse-transition"
                        ? `Upload at least 2 images for ${selectedAPI.name}`
                        : "Upload files to continue"
                      : "Ready to generate"}
                  </p>
                )}
              </Card>

              {/* Results - Fixed height container that never expands */}
              <Card className="p-4 bg-gray-800 border-gray-700 flex-shrink-0 flex flex-col uv-card flex-1 w-full">
                <h2 className="text-lg font-semibold text-white mb-3 flex-shrink-0">
                  Results
                </h2>
                <div className="flex-1 min-h-0 overflow-auto flex justify-center items-center w-full">
                  <ConversionResults
                    results={results}
                    isProcessing={isProcessing}
                    onSave={saveConvertedMedia}
                    isSaving={isSaving}
                    eventId={eventId}
                    loggedInUserObj={loggedInUser}
                  />
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <RenderSnackBar
        showToast={showToast}
        onClose={() => setShowToast(false)}
        toastConfig={{
          type: "success",
          message:
            "Creating your video — this may take a moment. You’ll find it in Production Media when it’s done.",
          duration: 5000,
        }}
      />
    </>
  );
};

export default AiStudio;
